import { useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";

const PEN_MIN_WIDTH = 1;
const PEN_MAX_WIDTH = 2;
const PLACEHOLDER_FONT = "italic 20px 'Helvetica Neue', Arial, sans-serif";
const PLACEHOLDER_COLOR = "lightgray";
const PLACEHOLDER_LINE_HEIGHT = 30;
const SHEET_COLOR = "#ffffff";
//250 minimum number of painted pixels to consider the signature drawn
const SIGNATURE_PIXEL_THRESHOLD = 250;

type UseSignaturePadParams = {
    // Lines drawn in light gray behind the signature, centered on the canvas
    placeholderLines: Array<string>;
};

// The canvas is the only source of truth about what will really be sent
const countPaintedPixels = (canvas: HTMLCanvasElement): number => {
    const context = canvas.getContext("2d");

    if (!context) {
        return 0;
    }

    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    let paintedPixels = 0;

    for (let index = 0; index < data.length; index += 4) {
        const alpha = data[index + 3];

        if (alpha > 0) {
            paintedPixels++;
        }
    }

    return paintedPixels;
};

/**
 * Owns the two stacked canvases of the signature screen: the one the user draws
 * on and the placeholder layer behind it.
 */
export const useSignaturePad = ({
    placeholderLines,
}: UseSignaturePadParams) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const underLayerCanvasRef = useRef<HTMLCanvasElement>(null);
    const signaturePadRef = useRef<SignaturePad | null>(null);
    const placeholderLinesRef = useRef<Array<string>>(placeholderLines);

    const [isEmpty, setIsEmpty] = useState(true);

    const placeholderKey = placeholderLines.join("\n");

    useEffect(() => {
        const canvas = canvasRef.current;

        if (!canvas) {
            return;
        }

        const signaturePad = new SignaturePad(canvas, {
            minWidth: PEN_MIN_WIDTH,
            maxWidth: PEN_MAX_WIDTH,
        });
        signaturePadRef.current = signaturePad;
        signaturePad.addEventListener("endStroke", refreshEmptyState);

        // Initial sizing
        resizeCanvases();

        window.addEventListener("resize", resizeCanvases);

        return () => {
            window.removeEventListener("resize", resizeCanvases);
            signaturePad.removeEventListener("endStroke", refreshEmptyState);
            signaturePad.off();
            signaturePadRef.current = null;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Its own canvas layer, so a language change never touches the signature
    useEffect(() => {
        placeholderLinesRef.current = placeholderLines;
        drawPlaceholder();
    }, [placeholderKey]); // eslint-disable-line react-hooks/exhaustive-deps

    function resizeCanvases() {
        const canvas = canvasRef.current;
        const underLayerCanvas = underLayerCanvasRef.current;
        const canvasWrapper = canvas?.parentElement;

        if (!canvas || !underLayerCanvas || !canvasWrapper) {
            return;
        }

        const wrapperWidth = canvasWrapper.offsetWidth;
        const wrapperHeight = canvasWrapper.offsetHeight;
        const hasSameSize =
            canvas.width === wrapperWidth && canvas.height === wrapperHeight;

        // Resizing wipes the drawing buffer: never do it for nothing
        if (hasSameSize) {
            return;
        }

        const strokes = signaturePadRef.current?.toData() ?? [];

        canvas.width = wrapperWidth;
        canvas.height = wrapperHeight;
        underLayerCanvas.width = wrapperWidth;
        underLayerCanvas.height = wrapperHeight;

        // Replay the strokes lost with the previous buffer
        if (strokes.length > 0) {
            signaturePadRef.current?.fromData(strokes);
        }

        drawPlaceholder();
        refreshEmptyState();
    }

    function drawPlaceholder() {
        const underLayerCanvas = underLayerCanvasRef.current;
        const context = underLayerCanvas?.getContext("2d");

        if (!underLayerCanvas || !context) {
            return;
        }

        /*
          The sheet is painted in the canvas bitmap instead of being left to the
          page background, because browsers that force a dark theme (Samsung
          Internet "Force Dark", Chrome Android "Auto Dark Theme") invert CSS
          colors but leave canvas content untouched. The background turned black
          while the stroke, drawn in the canvas, stayed black: the signature was
          invisible. Filling here keeps the contrast whatever the color scheme,
          and repaints the layer from scratch at the same time.
        */
        context.fillStyle = SHEET_COLOR;
        context.fillRect(0, 0, underLayerCanvas.width, underLayerCanvas.height);

        context.font = PLACEHOLDER_FONT;
        context.fillStyle = PLACEHOLDER_COLOR;
        // Horizontal and vertical alignment, so the lines are centered
        context.textAlign = "center";
        context.textBaseline = "middle";

        const x = underLayerCanvas.width / 2;
        const y = underLayerCanvas.height / 2;

        placeholderLinesRef.current.forEach((line, index) => {
            context.fillText(line, x, y + index * PLACEHOLDER_LINE_HEIGHT);
        });
    }

    function refreshEmptyState() {
        setIsEmpty(signaturePadRef.current?.isEmpty() ?? true);
    }

    function clear() {
        signaturePadRef.current?.clear();
        setIsEmpty(true);
        drawPlaceholder();
    }

    // Checked at click time, so an erased signature cannot leave as a blank
    function isSignatureDrawn() {
        const canvas = canvasRef.current;
        const signaturePad = signaturePadRef.current;

        if (!canvas || !signaturePad || signaturePad.isEmpty()) {
            return false;
        }

        return countPaintedPixels(canvas) >= SIGNATURE_PIXEL_THRESHOLD;
    }

    function getSignatureBlob(): Promise<Blob | null> {
        const canvas = canvasRef.current;

        if (!canvas) {
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), "image/png");
        });
    }

    return {
        canvasRef,
        underLayerCanvasRef,
        isEmpty,
        clear,
        isSignatureDrawn,
        getSignatureBlob,
    };
};
